// Admin surface for OTA live updates (the `config/ota` doc). Bundles are uploaded
// by the release script (scripts/release-ota.mjs); this panel gives full visual
// confidence over them — a live status header + health check (OtaStatusCard), the
// release history with one-tap rollback (OtaHistoryList), and the gate flags
// (OtaControls). Native players pull the live bundle on next open; web is never
// updated this way. Logic lives in useOtaAdmin so these pieces stay presentational.
import { Panel, Pill } from './parts';
import { OtaControls } from './ota/OtaControls';
import { OtaHistoryList } from './ota/OtaHistoryList';
import { OtaStatusCard } from './ota/OtaStatusCard';
import { useOtaAdmin } from './ota/useOtaAdmin';

export default function OtaReleasePanel() {
  const admin = useOtaAdmin();
  const { stored, available } = admin;

  const statusPill = stored?.enabled ? (
    <Pill tone="accent">Live · v{stored.version}</Pill>
  ) : (
    <Pill>Off</Pill>
  );

  return (
    <Panel title="Live updates (OTA)" action={statusPill} bodyClassName="p-4 md:p-5">
      <p className="text-[13px] leading-[1.5] text-ink2">
        Ship web-only changes to the iOS app without an App Store review. Upload a bundle with{' '}
        <code className="rounded bg-wash px-1">scripts/release-ota.mjs</code>, then set it live
        below. Native code changes still need a store submission; the web app is never updated this
        way.
      </p>

      {available.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-black/10 bg-wash px-4 py-6 text-center">
          <div className="text-[13px] font-semibold text-ink">No bundles uploaded yet</div>
          <div className="mt-1 text-[12px] text-ink3">
            Run <code className="rounded bg-card px-1">npm run build -w web</code> then{' '}
            <code className="rounded bg-card px-1">node scripts/release-ota.mjs</code> to publish the
            first bundle.
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <OtaStatusCard admin={admin} />
          <OtaHistoryList admin={admin} />
          <OtaControls admin={admin} />
        </div>
      )}
    </Panel>
  );
}
